import { ErreurApi, type Client } from '../../api/client.js';
import { creerSourceDossiers } from '../dossier/source-liste.js';
import { listerTousLesDossiers, lireParDossier } from '../dossier/agregat-par-dossier.js';
import type { Argent, VueEquipe } from './types.js';

/**
 * Liste transversale de la facturation (D08, équipe) : le contrat n'a pas de route « tous dossiers » ; elle agrège donc par dossier
 * (`dossiers.lister` puis `facturation.dossier` de chacun). Aucun montant n'est déduit : un dossier sans règle ou illisible est dit tel quel.
 */
export type EtatLigneFacturation = 'ok' | 'regles_absentes' | 'indisponible';

export interface LigneFacturation {
  dossierId: string;
  titre: string;
  reference: string;
  etat: EtatLigneFacturation;
  facture: Argent | null;
  encaisse: Argent | null;
  reste: Argent | null;
  /** Héritiers dont le reste dû est impayé. */
  nbImpayes: number;
  nbBloquantSignature: number;
}

export interface SourceListeFacturation {
  charger: () => Promise<LigneFacturation[]>;
}

const euros = (amountCents: number): Argent => ({ amountCents, currency: 'EUR' });

export const aTraiterFacturation = (l: LigneFacturation): boolean => l.etat === 'ok' && (l.nbImpayes > 0 || l.nbBloquantSignature > 0);

export function creerSourceListeFacturation(client: Client): SourceListeFacturation {
  return {
    async charger() {
      const dossiers = await listerTousLesDossiers(creerSourceDossiers(client));
      const lectures = await lireParDossier(dossiers, (d) =>
        client.appeler<VueEquipe>('facturation.dossier', { parametres: { dossierId: d.id } }),
      );
      return lectures.map((r): LigneFacturation => {
        const base = { dossierId: r.dossier.id, titre: r.dossier.title, reference: r.dossier.reference };
        if (!r.ok) {
          const sansRegle = r.erreur instanceof ErreurApi && r.erreur.code === 'NOT_FOUND';
          return {
            ...base,
            etat: sansRegle ? 'regles_absentes' : 'indisponible',
            facture: null,
            encaisse: null,
            reste: null,
            nbImpayes: 0,
            nbBloquantSignature: 0,
          };
        }
        const v = r.valeur;
        return {
          ...base,
          etat: 'ok',
          facture: euros(v.invoices.filter((f) => f.status !== 'annulee_par_avoir').reduce((s, f) => s + f.total.amountCents, 0)),
          encaisse: v.collected,
          reste: v.outstanding,
          nbImpayes: v.perHeir.filter((h) => h.status === 'impaye').length,
          nbBloquantSignature: v.perHeir.filter((h) => h.blocksSignature).length,
        };
      });
    },
  };
}
